import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Flex,
  FormContainer,
  Icon,
  notify,
  Text,
} from '@wyz/ui';
import { BlobHelpers } from '@wyz/utils';
import { useForm } from 'react-hook-form';
import { DropzoneAreaField } from '~/common/components';
import {
  Trans,
  useApplicationContext,
  useConfig,
  useI18n,
} from '~/common/context';
import { importTransportItemSchema } from '~/common/schemas';
import { IImportedEan, TranslationKeys, ImportFormData } from '~/common/types';
import { useManageTransportItems } from '~/container-order/api';
import { ContainerTracking } from '~/container-order/helpers';
import { usePurchaseErrorTranslation } from '~/common/hooks';

type UploadFormProps = {
  onClose: () => void;
  orderId: string;
  itemId: string;
  onShowPricing: (importedEans: Array<IImportedEan>, file: File) => void;
};

const FIELD_LOOKUP: Record<string, keyof TranslationKeys> = {
  ean: 'app.common.product.ean',
  quantity: 'app.common.quantity',
  transportItemId: 'app.container.order.detail.container_id',
};

function UploadForm({
  onClose,
  orderId,
  itemId,
  onShowPricing,
}: UploadFormProps) {
  const { auth } = useApplicationContext();
  const { apiUrl, tenantCode } = useConfig();
  const { t } = useI18n();
  const { resolveErrorKey } = usePurchaseErrorTranslation();
  const manageTransportItems = useManageTransportItems({
    import: { enabled: true },
    updateLines: { enabled: true },
  });

  const handleDownloadTemplate = () => {
    BlobHelpers.download({
      url: `${apiUrl}/offers/api/tenants/${tenantCode}/users/${auth.userId}/container-orders/import/template`,
      token: auth.user?.access_token ?? '',
    }).catch(() => {
      notify.error(t('app.common.messages.error'), {
        id: 'toast-error-template-download',
      });
    });
  };

  const methods = useForm<ImportFormData>({
    resolver: zodResolver(importTransportItemSchema),
    defaultValues: { file: undefined },
  });

  return (
    <FormContainer
      methods={methods}
      submitHandler={(formData) => {
        manageTransportItems.import.mutate(
          {
            file: formData.file,
            itemId,
            orderId,
          },
          {
            onSuccess: (response) => {
              if (response.meta.errors && response.meta.errors.length > 0) {
                const errorByEan = response.meta.errors.reduce(
                  (acc, error) => {
                    acc[error.value as string] = error.errors;
                    return acc;
                  },
                  {} as Record<string, Array<string>>,
                );
                const importedEans = response.data.map((item) => ({
                  ...item,
                  needsPriceUpdate: !!errorByEan[item.ean],
                }));

                onShowPricing(importedEans, formData.file);
              } else {
                manageTransportItems.updateLines.mutate(
                  {
                    orderId,
                    itemId,
                    body: {
                      lines: response.data.map((item) => ({
                        ean: item.ean,
                        quantity: Number(item.quantity),
                        price: undefined,
                      })),
                    },
                  },
                  {
                    onSuccess() {
                      ContainerTracking.trackFileUpload();
                      notify.success(
                        <Trans i18nKey='app.common.messages.success' />,
                        { id: 'toast-success-transport-item-lines-update' },
                      );
                      onClose();
                    },
                    onError() {
                      notify.error(
                        <Trans i18nKey='app.common.messages.error' />,
                        { id: 'toast-error-transport-item-lines-update' },
                      );
                    },
                  },
                );
              }
            },
            onError: () => {
              notify.error(
                t('app.common.messages.error') ||
                  'Failed to import container order',
                { id: 'toast-error-transport-item-import' },
              );
            },
          },
        );
      }}
    >
      <div className='text-center'>
        <Text heading='sm' color='secondary-gray'>
          <Trans i18nKey='app.container.order.import_modal.warning' />
        </Text>
      </div>

      <DropzoneAreaField
        name='file'
        filesLimit={1}
        label={''}
        acceptedFiles='XLSX'
      />

      <Flex className='mt-section'>
        <Button
          id='download-template'
          variant='link'
          onClick={handleDownloadTemplate}
        >
          <Trans i18nKey='app.common.buttons.download_template' />{' '}
          <Icon icon='download' className='ml-1' />
        </Button>
      </Flex>
      <Flex className='space-x-2'>
        <Button
          id='cancel-import'
          variant='secondary'
          disabled={
            manageTransportItems.import.isLoading ||
            manageTransportItems.updateLines.isLoading
          }
          onClick={onClose}
          fullWidth
        >
          <Trans i18nKey='app.common.button.cancel' />
        </Button>
        <Button
          id='submit-import'
          variant='primary'
          type='submit'
          isLoading={manageTransportItems.import.isLoading}
          fullWidth
        >
          <Trans i18nKey='app.common.buttons.validate' />
        </Button>
      </Flex>
      {manageTransportItems.import.error && (
        <div className='mt-4'>
          {manageTransportItems.import.error.errors.map((error) => {
            const field = error.propertyPath.split('.').at(-1) || '';
            return (
              <Alert variant='danger' key={error.propertyPath}>
                {`#${(error.context?.lineIndex ?? 0) + 1}: `}
                <Trans
                  i18nKey={resolveErrorKey(error.errors.at(0))}
                  values={{
                    field: t(FIELD_LOOKUP[field] || field),
                  }}
                />
              </Alert>
            );
          })}
        </div>
      )}
    </FormContainer>
  );
}

export default UploadForm;
